import { formatCurrency, HttpClient, prop } from 'common-utils';
import { User } from 'sso';
import { z } from 'zod';

import { DownloadState, PaymentStatus } from '../common-types';
import {
  DocumentPreview,
  parseDocumentPreview,
} from '../document-preview/document-preview-parsing';
import { localeList, localeListType } from '../payments/currency';

export type PreflightCoreApiClient = ReturnType<
  typeof createPreflightCoreApiClient
>;

export function createPreflightCoreApiClient(httpClient: HttpClient) {
  return {
    getSubmissionInfo: getSubmissionInfo.bind(undefined, httpClient),
    createDocumentId: createDocumentId.bind(undefined, httpClient),
    preparePayment: preparePayment.bind(undefined, httpClient),
    getDocumentProcess: getDocumentProcess.bind(undefined, httpClient),
    getFetchResult: getFetchResult.bind(undefined, httpClient),
    getDocumentPreview: getDocumentPreview.bind(undefined, httpClient),
    updateUser: updateUser.bind(undefined, httpClient),
    getUser: getUser.bind(undefined, httpClient),
    signOut: signOut.bind(undefined, httpClient),
  };
}

function getDocumentPreview(
  httpClient: HttpClient,
  documentId: DocumentId,
): Promise<DocumentPreview> {
  return httpClient
    .get(`/submission/${documentId.trim()}/suggestions`)
    .then(parseDocumentPreview);
}

export type getSubmissionInfoType = {
  documentId: DocumentId;
};

function getSubmissionInfo(
  httpClient: HttpClient,
  { documentId }: getSubmissionInfoType,
  currencyCode?: string,
): Promise<SubmissionInfo> {
  if (currencyCode && currencyCode !== '') {
    return httpClient
      .get(
        `/submission/${documentId.trim()}/info?currencyCode=${
          currencyCode ? currencyCode : ''
        }`,
      )
      .then(SubmissionInfo.parse);
  } else {
    return httpClient
      .get(`/submission/${documentId.trim()}/info`)
      .then(SubmissionInfo.parse);
  }
}

const PeerPilotEligibilitySchema = z
  .object({
    eligibleForPreview: z.boolean().optional(),
    eligibleForReport: z.boolean().optional(),
    report: z.boolean().optional(),
    preview: z.boolean().optional(),
    mode: z.enum(['DISABLED', 'MANUAL', 'AUTO']).optional(),
  })
  .optional();

export type SubmissionInfo = z.infer<typeof SubmissionInfo>;

const SubmissionInfo = z.union([
  z
    .object({
      downloadAvailableDays: z.number(),
      finalPrice: z.number(),
      status: z.enum(['REQUIRES_DOCUMENT']),
      currency: z.string().toUpperCase(),
      fileUploadDate: z.string(),
      tittle: z.string().optional(),
      salePrice: z.number().optional(),
      discountPrice: z.number().optional(),
      discountApplied: z.boolean().optional(),
      addOnServiceOrderId: z.string().optional(),
      addOnServiceList: z
        .array(
          z.object({
            addOnKey: z.string().optional(),
            basePrice: z.number().optional(),
            salePrice: z.number().optional(),
            enabled: z.boolean().optional(),
            title: z.string().optional(),
            addOnServiceId: z.number().optional(),
            gjdJournalName: z.string().optional(),
            gjdPublisherName: z.string().optional(),
            status: z.enum(['PENDING', 'COMPLETED']).optional(),
            noOfImages: z.number().optional(),
          }),
        )
        .optional(),
      peerPilotEligibility: PeerPilotEligibilitySchema,
    })
    .transform((submissionInfo) => ({
      ...submissionInfo,
      orderId: submissionInfo.addOnServiceOrderId
        ? submissionInfo.addOnServiceOrderId
        : '',
      salePriceOriginal: submissionInfo.salePrice,
      finalPriceOriginal: submissionInfo.finalPrice,
      finalPrice: submissionInfo.addOnServiceList?.length
        ? formatCurrency(
            submissionInfo.addOnServiceList.reduce(
              (acc, addOn) => acc + (addOn.salePrice || 0),
              0,
            ) / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          )
        : formatCurrency(
            submissionInfo.finalPrice / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          ),
      salePrice: formatCurrency(
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) / 100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountPrice: formatCurrency(
        ((submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice) /
          100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountApplied:
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice >
        0
          ? true
          : false,
    })),
  z
    .object({
      downloadAvailableDays: z.number(),
      finalPrice: z.number(),
      status: z.enum(['PROCESSING_FAILED']),
      currency: z.string().toUpperCase(),
      fileUploadDate: z.string(),
      tittle: z.string().optional(),
      salePrice: z.number().optional(),
      discountPrice: z.number().optional(),
      discountApplied: z.boolean().optional(),
      addOnServiceOrderId: z.string().optional(),
      addOnServiceList: z
        .array(
          z.object({
            addOnKey: z.string().optional(),
            basePrice: z.number().optional(),
            salePrice: z.number().optional(),
            enabled: z.boolean().optional(),
            title: z.string().optional(),
            addOnServiceId: z.number().optional(),
            gjdJournalName: z.string().optional(),
            gjdPublisherName: z.string().optional(),
            status: z.enum(['PENDING', 'COMPLETED']).optional(),
            noOfImages: z.number().optional(),
          }),
        )
        .optional(),
      peerPilotEligibility: PeerPilotEligibilitySchema,
    })
    .transform((submissionInfo) => ({
      ...submissionInfo,
      orderId: submissionInfo.addOnServiceOrderId
        ? submissionInfo.addOnServiceOrderId
        : '',
      salePriceOriginal: submissionInfo.salePrice,
      finalPriceOriginal: submissionInfo.finalPrice,
      finalPrice: submissionInfo.addOnServiceList?.length
        ? formatCurrency(
            submissionInfo.addOnServiceList.reduce(
              (acc, addOn) => acc + (addOn.salePrice || 0),
              0,
            ) / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          )
        : formatCurrency(
            submissionInfo.finalPrice / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          ),
      salePrice: formatCurrency(
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) / 100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountPrice: formatCurrency(
        ((submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice) /
          100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountApplied:
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice >
        0
          ? true
          : false,
    })),
  z
    .object({
      editedFileName: z.string(),
      finalPrice: z.number(),
      downloadAvailableDays: z.number(),
      downloadState: DownloadState,
      paymentStatus: PaymentStatus,
      status: z.enum(['REQUIRES_PAYMENT']),
      currency: z.string().toUpperCase(),
      fileUploadDate: z.string(),
      tittle: z.string().optional(),
      salePrice: z.number().optional(),
      discountPrice: z.number().optional(),
      discountApplied: z.boolean().optional(),
      addOnServiceOrderId: z.string().optional(),
      addOnServiceList: z
        .array(
          z.object({
            addOnKey: z.string().optional(),
            basePrice: z.number().optional(),
            salePrice: z.number().optional(),
            enabled: z.boolean().optional(),
            title: z.string().optional(),
            addOnServiceId: z.number().optional(),
            gjdJournalName: z.string().optional(),
            gjdPublisherName: z.string().optional(),
            status: z.enum(['PENDING', 'COMPLETED']).optional(),
            noOfImages: z.number().optional(),
          }),
        )
        .optional(),
      peerPilotEligibility: PeerPilotEligibilitySchema,
    })
    .transform((submissionInfo) => ({
      ...submissionInfo,
      orderId: submissionInfo.addOnServiceOrderId
        ? submissionInfo.addOnServiceOrderId
        : '',
      salePriceOriginal: submissionInfo.salePrice,
      finalPriceOriginal: submissionInfo.finalPrice,
      finalPrice: submissionInfo.addOnServiceList?.length
        ? formatCurrency(
            submissionInfo.addOnServiceList.reduce(
              (acc, addOn) => acc + (addOn.salePrice || 0),
              0,
            ) / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          )
        : formatCurrency(
            submissionInfo.finalPrice / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          ),
      salePrice: formatCurrency(
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) / 100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountPrice: formatCurrency(
        ((submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice) /
          100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountApplied:
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice >
        0
          ? true
          : false,
    })),
  z
    .object({
      editedFileName: z.string(),
      finalPrice: z.number(),
      downloadAvailableDays: z.number(),
      downloadState: DownloadState,
      paymentStatus: PaymentStatus,
      status: z.enum(['COMPLETED']),
      currency: z.string().toUpperCase(),
      fileUploadDate: z.string(),
      tittle: z.string().optional(),
      salePrice: z.number().optional(),
      discountPrice: z.number().optional(),
      discountApplied: z.boolean().optional(),
      addOnServiceOrderId: z.string().optional(),
      addOnServiceList: z
        .array(
          z.object({
            addOnKey: z.string().optional(),
            basePrice: z.number().optional(),
            salePrice: z.number().optional(),
            enabled: z.boolean().optional(),
            title: z.string().optional(),
            addOnServiceId: z.number().optional(),
            gjdJournalName: z.string().optional(),
            gjdPublisherName: z.string().optional(),
            status: z.enum(['PENDING', 'COMPLETED']).optional(),
            noOfImages: z.number().optional(),
          }),
        )
        .optional(),
      peerPilotEligibility: PeerPilotEligibilitySchema,
    })
    .transform((submissionInfo) => ({
      ...submissionInfo,
      orderId: submissionInfo.addOnServiceOrderId
        ? submissionInfo.addOnServiceOrderId
        : '',
      salePriceOriginal: submissionInfo.salePrice,
      finalPriceOriginal: submissionInfo.finalPrice,
      finalPrice: submissionInfo.addOnServiceList?.length
        ? formatCurrency(
            submissionInfo.addOnServiceList.reduce(
              (acc, addOn) => acc + (addOn.salePrice || 0),
              0,
            ) / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          )
        : formatCurrency(
            submissionInfo.finalPrice / 100,
            localeList[submissionInfo.currency as keyof localeListType],
            submissionInfo.currency,
          ),
      salePrice: formatCurrency(
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) / 100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountPrice: formatCurrency(
        ((submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice) /
          100,
        localeList[submissionInfo.currency as keyof localeListType],
        submissionInfo.currency,
      ),
      discountApplied:
        (submissionInfo.salePrice ? submissionInfo.salePrice : 0) -
          submissionInfo.finalPrice >
        0
          ? true
          : false,
    })),
]);

type createDocumentIdType = {
  journalId: string;
  publisherId: string;
  editorialDesk?: boolean;
};

function createDocumentId(
  httpClient: HttpClient,
  { journalId, publisherId, editorialDesk }: createDocumentIdType,
): Promise<DocumentIdPayload> {
  const prefix = editorialDesk ? '/ppe' : '';
  return httpClient
    .get(`${prefix}/publisher/${publisherId}/journal/${journalId}/upload/id`)
    .then(DocumentIdPayload.parse);
}

const UUID = z.string().uuid();

type UUID = z.infer<typeof UUID>;

export const DocumentId = z.custom<`realtime-${UUID}`>((value) => {
  if (value === undefined) {
    return false;
  }

  const prefix = (value as string).slice(0, 9);
  const uuid = (value as string).slice(9);

  return prefix === 'realtime-' && UUID.safeParse(uuid).success;
});

export type DocumentId = z.infer<typeof DocumentId>;

export const DocumentIdPayload = z.object({
  documentId: DocumentId,
});

type DocumentIdPayload = z.infer<typeof DocumentIdPayload>;

type preparePaymentType = {
  documentId: DocumentId;
  currency?: string;
  pageUrl?: string;
  isAddonPending?: boolean;
};
function preparePayment(
  httpClient: HttpClient,
  { documentId, currency, pageUrl, isAddonPending = false }: preparePaymentType,
): Promise<PaymentSession> {
  let url = `/payment/${documentId.trim()}/prepare`;
  if (isAddonPending) {
    url = `/payment/${documentId.trim()}/addon-services/prepare`;
  }

  if (currency && pageUrl) {
    url += `?currencyCode=${currency}&origin=${pageUrl}`;
  } else if (currency) {
    url += `?currencyCode=${currency}`;
  } else if (pageUrl) {
    url += `?origin=${pageUrl}`;
  }

  return httpClient
    .post(url)
    .then(PaymentPreparePayload.parse)
    .then(prop('intent'));
}

export type PaymentSession = z.infer<typeof PaymentSession>;

const PaymentSession = z.object({
  paymentIntentSecret: z.string(),
  stripePublishableKey: z.string(),
});

const PaymentPreparePayload = z.object({
  intent: PaymentSession,
});

function getDocumentProcess(
  httpClient: HttpClient,
  documentId: DocumentId,
  isV1 = false,
) {
  const endpoint = isV1
    ? `/v2/checks/${documentId.trim()}/perform-checks`
    : `/checks/${documentId.trim()}/perform-checks`;

  return httpClient.get(endpoint).then(DocumentProcess.parse);
}

function getFetchResult(
  httpClient: HttpClient,
  documentId: DocumentId,
  clientToken: string,
) {
  return httpClient
    .get(
      `/editorial-manager/checks/${documentId}/generate-result?token=${clientToken}`,
    )
    .then(FetchResult.parse);
}

export type DescriptionLinks = z.infer<typeof DescriptionLinks>;

const DescriptionLinks = z.array(
  z.object({
    text: z.string(),
    url: z.string(),
  }),
);

export type RawCompletedCheck = z.infer<typeof RawCompletedCheck>;

const RawCompletedCheck = z.object({
  checkKey: z.string(),
  description: z.object({
    text: z.string(),
    links: DescriptionLinks.optional(),
  }),
  label: z.string(),
  result: z.enum(['CRITICAL', 'PASSED', 'WARNING']),
});

export type PeerPilotCheck = z.infer<typeof PeerPilotCheck>;
const PeerPilotCheck = z.object({
  checkKey: z.string(),
  result: z.enum(['CRITICAL', 'PASSED', 'WARNING', 'UNKNOWN']),
  grouping: z.string(),
  groupingIndex: z.number(),
  category: z.string(),
  groupSubSection: z.string().optional(),
  groupSubSectionLabel: z.string().optional(),
  additionalDescription: z
    .object({
      summary: z.string(),
      additionalDataHeader: z.string().optional(),
      additionalData: z
        .record(z.string(), z.array(z.string()).optional())
        .optional(),
      additionalDataObject: z
        .record(z.string(), z.array(z.unknown()).optional())
        .optional(),
      supplementaryDataHeader: z.string().optional(),
      supplementaryData: z.array(z.string()).optional(),
      additionalImageData: z
        .array(
          z.object({
            imageUrl: z.string().optional(),
            imageCaption: z.record(z.string(), z.string().optional()),
            additionalInformation: z.record(z.string(), z.string().optional()),
          }),
        )
        .optional(),
    })
    .optional(),
  passScore: z.number(),
  warningScore: z.number(),
  criticalScore: z.number(),
});

export type RawDocumentProcess = z.infer<typeof RawDocumentProcess>;

const RawDocumentProcess = z.object({
  completedChecks: z.array(RawCompletedCheck),
  peerPilotChecks: z.array(PeerPilotCheck).optional(),
  requestCompleted: z.boolean(),
  manuscriptId: z.string().optional(),
});

export type CompletedCheck = z.infer<typeof CompletedCheck>;

const CompletedCheck = z.object({
  checkKey: z.string(),
  description: z.object({
    text: z.string(),
    links: DescriptionLinks.optional(),
  }),
  label: z.string(),
  issues: z.number().optional(),
  category: z.string().optional(),
  result: z
    .enum(['CRITICAL', 'PASSED', 'WARNING', 'UNKNOWN'])
    .transform<'failed' | 'successful'>((result) =>
      result === 'PASSED' ? 'successful' : 'failed',
    ),
  groupSubSection: z.string().optional(),
  additionalDescription: z
    .object({
      summary: z.string(),
      additionalDataHeader: z.string().optional(),
      additionalData: z
        .record(z.string(), z.array(z.string()).optional())
        .optional(),
      additionalDataObject: z
        .record(z.string(), z.array(z.unknown()).optional())
        .optional(),
      supplementaryDataHeader: z.string().optional(),
      supplementaryData: z.array(z.string()).optional(),
      additionalImageData: z
        .array(
          z.object({
            imageUrl: z.string().optional(),
            imageCaption: z.record(z.string(), z.string().optional()),
            additionalInformation: z.record(z.string(), z.string().optional()),
          }),
        )
        .optional(),
    })
    .optional(),
  grouping: z.string().optional(),
});

export type DocumentProcess = z.infer<typeof DocumentProcess>;
export type FetchResult = z.infer<typeof FetchResult>;
export const SectionWiseResult = z.record(
  z.enum(['CRITICAL', 'PASSED', 'WARNING', 'UNKNOWN']),
);
export const SubSectionWiseResult = z.array(
  z.object({
    groupSubsection: z.string(),
    checkResult: z.string(),
    description: z.string(),
  }),
);
const DocumentProcess = z.object({
  completedChecks: z.array(CompletedCheck),
  peerPilotChecks: z.array(PeerPilotCheck).optional(),
  requestCompleted: z.boolean(),
  summaryResult: z.enum(['CRITICAL', 'PASSED', 'WARNING', 'UNKNOWN']),
  languageSuggestions: z.number(),
  sectionWiseResult: SectionWiseResult.optional(),
  subSectionWiseResults: SubSectionWiseResult.optional(),
  integrityCheckSummary: z.string().optional(),
  languageAccessibilityScore: z.string().optional(),
  languageSummaryResult: z
    .enum(['PASSED', 'WARNING', 'CRITICAL', 'UNKNOWN'])
    .optional(),
  technicalSummaryResult: z
    .enum(['PASSED', 'WARNING', 'CRITICAL', 'UNKNOWN'])
    .optional(),
  articleType: z
    .object({
      id: z.number().optional(),
      name: z.string().optional(),
      master: z.boolean().optional(),
    })
    .optional(),
  selectedModules: z.array(z.string()).optional(),
  selectedModuleResults: z
    .record(
      z.object({
        label: z.string().optional(),
        score: z.number().optional(),
        scoreLevel: z.string().optional(),
      }),
    )
    .optional(),
  detectAiModuleResult: z
    .object({
      label: z.string().optional(),
      score: z.number().optional(),
      scoreLevel: z.string().optional(),
    })
    .optional(),
  peerPilotModuleResult: z
    .object({
      label: z.string().optional(),
      score: z.number().optional(),
      scoreLevel: z.string().optional(),
    })
    .optional(),
  smartScreenModuleResult: z
    .enum(['CRITICAL', 'WARNING', 'PASSED', 'UNKNOWN'])
    .optional(),
  fileName: z.string().optional(),
  fileUploadDate: z.string().optional(),
  manuscriptId: z.string().optional(),
});

export type FetchResultCompletedCheck = z.infer<
  typeof FetchResultCompletedCheck
>;

const FetchResultCompletedCheck = z.object({
  extKey: z.string(),
  description: z.string(),
  checkLabel: z.string(),
  issues: z.number().optional(),
  category: z.string().optional(),
  result: z
    .enum(['CRITICAL', 'PASSED', 'WARNING', 'UNKNOWN'])
    .transform<'failed' | 'successful'>((result) =>
      result === 'PASSED' ? 'successful' : 'failed',
    ),
  groupSubSection: z.string().optional(),
  additionalDescription: z
    .object({
      summary: z.string(),
      additionalDataHeader: z.string().optional(),
      additionalData: z
        .record(z.string(), z.array(z.string()).optional())
        .optional(),
      additionalDataObject: z
        .record(z.string(), z.array(z.unknown()).optional())
        .optional(),
      supplementaryDataHeader: z.string().optional(),
      supplementaryData: z.array(z.string()).optional(),
      additionalImageData: z
        .array(
          z.object({
            imageUrl: z.string().optional(),
            imageCaption: z.record(z.string(), z.string().optional()),
            additionalInformation: z.record(z.string(), z.string().optional()),
          }),
        )
        .optional(),
    })
    .optional(),
  grouping: z.string(),
  groupingLabel: z.string().optional(),
});

const FetchResult = z.object({
  checkResponse: z.object({
    completedChecks: z.array(FetchResultCompletedCheck).optional().default([]),
    peerPilotChecks: z.array(PeerPilotCheck).optional(),
    status: z.boolean(),
    summaryResult: z.enum(['CRITICAL', 'PASSED', 'WARNING']),
    languageSuggestionsCount: z.number().optional(),
    sectionWiseResult: SectionWiseResult.optional(),
    subSectionWiseResults: SubSectionWiseResult.optional(),
    researchIntegritySummary: z.string().optional(),
    languageAccessibilityScore: z.string().optional(),
    editedFileName: z.string().optional(),
    selectedModules: z.array(z.string()).optional(),
    selectedModuleResults: z
      .record(
        z.object({
          label: z.string().optional(),
          score: z.number().optional(),
          scoreLevel: z.string().optional(),
        }),
      )
      .optional(),
    smartScreenModuleResult: z.string().optional(),
    languageSummaryResult: z.string().optional(),
    skippedModules: z
      .array(
        z.object({
          module: z.string(),
          reason: z.string(),
        }),
      )
      .optional(),
  }),
  rapidHumanReviewEnabled: z.boolean(),
  humanReviewEnabled: z.boolean(),
  journalLabel: z.string(),
});

export type CheckState = DocumentProcess['completedChecks'][0];
export type FetchResultCheckState =
  FetchResult['checkResponse']['completedChecks'][0];
export type PeerPilotCheckState = DocumentProcess['peerPilotChecks'];
export type FetchResultPeerPilotCheckState =
  FetchResult['checkResponse']['peerPilotChecks'];

type UpdateUser = {
  bigIntId?: string;
};

function updateUser(client: HttpClient, user: UpdateUser, authToken: string) {
  return client.patch(`/user/me`, user, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
}

function getUser(client: HttpClient, ssoToken: string) {
  return client
    .get('/user/me', {
      headers: {
        Authorization: `Bearer ${ssoToken}`,
      },
    })
    .then(User.parse);
}

function signOut(client: HttpClient) {
  return client.post('/user/logout');
}
